

























































import { Button } from 'element-ui'
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'

import axios from '@/axios-module'
import notification from '@/notification'
import { stripePaymentElementAppearance } from '@/utils/stripe-styles'

const METHOD_IDS = ['card', 'apple_pay', 'google_pay', 'link'] as const
type MethodId = typeof METHOD_IDS[number]
const RECREATE_SESSION_DEBOUNCE_MS = 300
const CURRENCY_SELECTOR_SKELETON_HOLD_MS = 500

interface Availability {
  card: boolean
  apple_pay: boolean
  google_pay: boolean
  link: boolean
}

interface CreateSessionResponse {
  checkoutSessionId: string
  clientSecret: string
  publishableKey: string
}

@Component({
  components: {
    [Button.name]: Button,
  },
})
export default class PaymentMethodSelectorCheckout extends Vue {
  @Prop() sdkLoaded!: boolean
  @Prop() useEurStripeAccount!: boolean
  @Prop({ default: 0 }) amount!: number
  @Prop({ default: 'usd' }) currency!: string
  @Prop({ default: '' }) bookingId!: string
  @Prop({ default: false }) termsAccepted!: boolean
  @Prop({ default: '' }) selectedMethodId!: string
  @Prop({ default: true }) paymentInteractionEnabled!: boolean
  @Prop({ default: false }) showElementLoader!: boolean
  @Prop({ default: true }) showCurrencySelector!: boolean
  @Prop({ default: false }) adaptivePricingAllowed!: boolean
  @Prop({ default: '' }) externalCurrencySelectorTarget!: string
  @Prop({ default: null }) externalExpressElementContainer!: HTMLElement | null
  @Prop({ default: true }) showPaymentElement!: boolean
  @Prop({ default: '' }) customerEmail!: string

  @Ref() elementContainer!: HTMLElement
  @Ref() expressProbeContainer!: HTMLElement
  @Ref() billingContainer!: HTMLElement

  elementStripeLoading = false

  private stripe: any = null
  private checkout: any = null
  private checkoutActions: any = null
  private checkoutSessionId: string | null = null
  private sessionClientSecret: string | null = null

  private availability: Availability = {
    card: true,
    apple_pay: false,
    google_pay: false,
    link: false,
  }
  private availabilityReady = false
  private selectedMethod: MethodId | '' = ''

  private currentPaymentElement: any = null
  private currentExpressElement: any = null
  private currentProbeExpressElement: any = null
  private currentBillingAddressElement: any = null
  private currentCurrencySelectorElement: any = null
  private paymentElementComplete = false
  private paymentElementEventsBound = false
  private mountGen = 0
  private recreateSessionDebounceTimer: ReturnType<
    typeof setTimeout
  > | null = null
  private recreateSessionRunId = 0

  @Watch('sdkLoaded')
  onSdkLoaded(value: boolean) {
    if (value) {
      this.bootstrap()
    }
  }

  @Watch('selectedMethod')
  async onSelectedMethodChange(newVal: MethodId | '', oldVal: MethodId | '') {
    if (oldVal) {
      this.unmountElement()
    }
    const switchedExpressMethod =
      oldVal !== newVal &&
      this.isExpressMethod(oldVal) &&
      this.isExpressMethod(newVal)
    if (switchedExpressMethod) {
      await this.recreateSessionForAmount() // rebuild checkout session before creating another express element
      this.$emit('method-change', newVal)
      return
    }
    if (newVal && this.showPaymentElement) {
      await this.$nextTick()
      if (this.isExpressMethod(newVal)) {
        this.tryMountExpressCheckout(newVal)
        this.$emit('method-change', newVal)
        return
      }
      this.mountElementForMethod(newVal)
    }
    this.$emit('method-change', newVal)
  }

  @Watch('selectedMethodId')
  onSelectedMethodIdChange(value: string) {
    const method = this.normalizeMethodId(value || '')
    if (method && this.selectedMethod !== method) {
      this.selectedMethod = method
    }
  }

  @Watch('amount')
  onAmountChange(newAmount: number, oldAmount: number) {
    if (newAmount === oldAmount || !newAmount || !this.bookingId) {
      return
    }
    this.scheduleRecreateSessionForAmount()
  }

  @Watch('adaptivePricingAllowed')
  async onAdaptivePricingAllowedChange() {
    if (!this.checkout) {
      return
    }
    await this.recreateSessionForAmount()
  }

  @Watch('customerEmail')
  async onCustomerEmailChange() {
    await this.syncBuyerEmailWithCheckout()
  }

  @Watch('externalCurrencySelectorTarget')
  async onExternalCurrencySelectorTargetChange(target: string) {
    if (!target) {
      this.parkCurrencySelector()
      return
    }
    if (!this.currentCurrencySelectorElement) {
      await this.mountCurrencySelector()
      return
    }
    this.emitCurrencySelectorReady(false)
    await this.$nextTick()
    try {
      this.currentCurrencySelectorElement.unmount()
      this.currentCurrencySelectorElement.mount(target)
      await this.holdCurrencySelectorSkeleton()
      this.emitCurrencySelectorReady(true)
    } catch (_) {
      // leave the element where it was rather than losing it
    }
  }

  @Watch('elementStripeLoading')
  @Watch('availabilityReady')
  @Watch('externalExpressElementContainer')
  @Watch('selectedMethod')
  onExternalExpressLoadingInputs() {
    this.syncExternalExpressLoadingState()
  }

  @Watch('externalExpressElementContainer')
  async onExternalExpressElementContainerChange(container: HTMLElement | null) {
    const method = this.normalizeMethodId(this.selectedMethodId)
    if (!method || !this.isExpressMethod(method)) {
      if (!container) {
        this.destroyExpressElement()
      }
      return
    }
    if (!container) {
      this.destroyExpressElement()
      return
    }
    if (this.selectedMethod !== method) {
      this.selectedMethod = method
    }
    await this.$nextTick()
    this.tryMountExpressCheckout(method)
  }

  mounted() {
    const method = this.normalizeMethodId(this.selectedMethodId)
    if (method) {
      this.selectedMethod = method
    }
    if (this.sdkLoaded) {
      this.bootstrap()
    }
  }

  beforeDestroy() {
    this.clearRecreateSessionDebounce()
    this.recreateSessionRunId += 1
    this.elementStripeLoading = false
    const checkout = this.checkout
    this.unmountElement()
    if (checkout) {
      this.destroyRegisteredBillingAddressElement(checkout)
    }
    this.unmountProbe()
    this.unmountCurrencySelector()
    this.checkout = null
    this.checkoutActions = null
  }

  refreshCheckoutSessionDisplay() {
    if (!this.checkoutActions) {
      return
    }
    this.emitCheckoutSessionDisplay(this.checkoutActions.getSession())
  }

  async submitAndConfirm(): Promise<boolean> {
    if (!this.paymentInteractionEnabled) {
      return false
    }
    if (this.selectedMethod !== 'card') {
      this.$emit('on-card-error', ['Please select Credit Card to pay'])
      return false
    }
    if (!this.termsAccepted) {
      this.$emit('on-card-error', ['Please accept the Terms & Conditions'])
      return false
    }
    if (!this.checkoutActions || !this.checkoutSessionId) {
      this.$emit('on-card-error', [
        'Please wait for the payment form to load, or try opening this step again.',
      ])
      return false
    }
    if (!(await this.passesAmountPrecheck())) {
      return false
    }
    try {
      const result = await this.checkoutActions.confirm({
        redirect: 'if_required',
      })
      if (this.isErrorResult(result)) {
        const message = this.extractErrorMessage(result)
        notification(message, 'error')
        this.$emit('on-card-error', [
          message,
          'The payment form was reset for a new attempt — please re-enter your details.',
        ])
        await this.recreateSessionForAmount()
        return false
      }
      this.emitCheckoutComplete()
      return true
    } catch (err: any) {
      const msg = err?.message || 'An unexpected error occurred'
      notification(msg, 'error')
      this.$emit('on-card-error', [msg])
      return false
    }
  }
  async validateBeforeProceed(): Promise<boolean> {
    if (this.selectedMethod !== 'card' && this.selectedMethodId === 'card') {
      this.selectedMethod = 'card'
      await this.$nextTick()
    }
    if (this.selectedMethod !== 'card') {
      this.$emit('on-card-error', ['Please select Credit Card to continue'])
      return false
    }
    if (!this.currentPaymentElement) {
      await this.$nextTick()
      if (!this.currentPaymentElement) {
        this.mountElementForMethod('card')
      }
    }
    if (!this.currentPaymentElement) {
      this.$emit('on-card-error', ['Please wait for the card form to load'])
      return false
    }
    if (!this.paymentElementComplete) {
      this.$emit('on-card-error', [
        'Please complete your card details before continuing.',
      ])
      return false
    }
    if (!(await this.isBillingAddressValid())) {
      this.$emit('on-card-error', [
        'Please complete the cardholder name and billing address before continuing.',
      ])
      return false
    }
    this.$emit('on-card-error', [])
    return true
  }

  private async passesAmountPrecheck(): Promise<boolean> {
    if (!this.bookingId || !this.amount) {
      return true
    }
    try {
      const {
        data,
      } = await axios.post(
        `/customer/bookings/${this.bookingId}/payments/validate_amount`,
        { amountCents: this.amount, currency: this.currency },
      )
      if (data && typeof data.outstandingCents === 'number') {
        this.$emit('outstanding-update', {
          outstandingCents: data.outstandingCents,
        })
      }
      if (data && data.allowed === false) {
        const message =
          data.reason ||
          'This amount can no longer be paid. Please review the amount and try again.'
        notification(message, 'error')
        this.$emit('on-card-error', [message])
        return false
      }
      return true
    } catch (_) {
      return true
    }
  }

  private async isBillingAddressValid(): Promise<boolean> {
    const element = this.currentBillingAddressElement
    if (!element) {
      return true
    }
    try {
      const result = await element.getValue()
      return !!result?.complete
    } catch (_) {
      return false
    }
  }

  get availableMethodsList(): { id: MethodId; label: string }[] {
    const labels: Record<MethodId, string> = {
      card: 'Card',
      apple_pay: 'Apple Pay',
      google_pay: 'Google Pay',
      link: 'Link',
    }
    return METHOD_IDS.filter((id) => this.availability[id]).map((id) => {
      return { id, label: labels[id] }
    })
  }

  get shouldShowElementLoader(): boolean {
    return (
      this.showElementLoader &&
      !!this.selectedMethod &&
      (!this.availabilityReady || this.elementStripeLoading)
    )
  }

  get shouldShowExternalExpressLoader(): boolean {
    return (
      !!this.externalExpressElementContainer &&
      this.isExpressMethod(this.selectedMethod) &&
      (!this.availabilityReady || this.elementStripeLoading)
    )
  }

  private normalizeMethodId(value: string): MethodId | '' {
    return (METHOD_IDS as readonly string[]).includes(value)
      ? (value as MethodId)
      : ''
  }

  private isExpressMethod(method: MethodId | ''): boolean {
    return (
      method === 'apple_pay' || method === 'google_pay' || method === 'link'
    )
  }

  private syncExternalExpressLoadingState() {
    this.$emit('express-element-loading', this.shouldShowExternalExpressLoader)
  }

  /** Express wallets only render on the confirm-step host from the parent page. */
  private tryMountExpressCheckout(method: MethodId) {
    if (!this.externalExpressElementContainer) {
      return
    }
    this.mountElementForMethod(method)
  }

  private isErrorResult(result: any): boolean {
    if (!result) {
      return false
    }
    if (result.type === 'error') {
      return true
    }
    return !!result.error
  }

  private extractErrorMessage(result: any): string {
    return (
      result?.error?.message ||
      result?.error?.payment_intent?.last_payment_error?.message ||
      result?.paymentIntent?.last_payment_error?.message ||
      result?.message ||
      'Payment failed. Please try again.'
    )
  }

  private emitCheckoutComplete() {
    if (!this.checkoutSessionId) {
      return
    }
    this.$emit('on-checkout-complete', {
      checkoutSessionId: this.checkoutSessionId,
    })
  }

  private initStripe(publishableKey?: string) {
    const pk =
      publishableKey ||
      (this.useEurStripeAccount
        ? process.env.VUE_APP_STRIPE_EUR_ACC_PK
        : process.env.VUE_APP_STRIPE_PK)
    this.stripe =
      typeof pk === 'string' ? Stripe(pk, { locale: 'en-US' }) : null
  }

  private initializeCardOnlyAvailability() {
    this.availability = {
      card: true,
      apple_pay: false,
      google_pay: false,
      link: false,
    }
    this.availabilityReady = true
    this.$emit('available-methods', this.availableMethodsList)
    const preferred = this.normalizeMethodId(this.selectedMethodId)
    this.selectedMethod = preferred || 'card'
  }

  private async bootstrap(runId?: number) {
    const isStaleRun = () =>
      typeof runId === 'number' && runId !== this.recreateSessionRunId

    if (!this.amount || !this.bookingId) {
      this.initializeCardOnlyAvailability()
      return
    }
    let bootstrapError: any = null
    let stripeUnavailable = false
    try {
      const session = await this.createSession()
      this.initStripe(session.publishableKey)
      if (!this.stripe) {
        stripeUnavailable = true
      } else {
        await this.bootstrapCheckout()
        if (this.externalCurrencySelectorTarget) {
          await this.mountCurrencySelector()
        }
      }
    } catch (err: any) {
      bootstrapError = err
    }

    if (isStaleRun()) {
      return
    }

    if (bootstrapError) {
      const msg =
        bootstrapError?.response?.data?.error ||
        bootstrapError?.message ||
        'Failed to load payment form'
      notification(msg, 'error')
      this.$emit('on-card-error', [msg])
      this.initializeCardOnlyAvailability()
      return
    }

    if (stripeUnavailable) {
      this.initializeCardOnlyAvailability()
      return
    }

    await this.initializeAvailability()
  }

  private async createSession(): Promise<CreateSessionResponse> {
    const previousCheckoutSessionId = this.checkoutSessionId
    const url = `/customer/bookings/${this.bookingId}/payments/create_checkout_session`
    const trimmedEmail = this.customerEmail?.trim() || ''
    const { data } = await axios.post(url, {
      amountCents: this.amount,
      currency: this.currency,
      previousCheckoutSessionId,
      ...(trimmedEmail ? { customerEmail: trimmedEmail } : {}),
    })

    this.checkoutSessionId = data.checkoutSessionId
    this.sessionClientSecret = data.clientSecret
    this.$emit('checkout-session-created', {
      checkoutSessionId: data.checkoutSessionId,
      clientSecret: data.clientSecret,
      publishableKey: data.publishableKey,
    })
    return data as CreateSessionResponse
  }

  /**
   * Stripe only fills `session.currencyOptions` when Adaptive Pricing is
   * allowed, and allowing it converts the session to the local currency for
   * good. So allow it to find out whether AP exists, then start a fresh
   * session in the original currency at first.
   */
  private async bootstrapCheckout() {
    if (!this.stripe || !this.sessionClientSecret) {
      return
    }
    await this.initCheckoutSdk({ adaptivePricingAllowed: true })
    await this.syncBuyerEmailWithCheckout()

    const adaptivePricingOffered = this.sessionHasCurrencyOptions()
    this.$emit('adaptive-pricing-offered', adaptivePricingOffered)
    if (adaptivePricingOffered && !this.adaptivePricingAllowed) {
      await this.createSession()
      await this.initCheckoutSdk({ adaptivePricingAllowed: false })
      await this.syncBuyerEmailWithCheckout()
    }

    this.bindCheckoutSessionChangeListener()
    this.refreshCheckoutSessionDisplay()
  }

  private async initCheckoutSdk(options: { adaptivePricingAllowed: boolean }) {
    const result = this.stripe.initCheckoutElementsSdk({
      clientSecret: this.sessionClientSecret,
      adaptivePricing: { allowed: options.adaptivePricingAllowed },
      elementsOptions: {
        appearance: stripePaymentElementAppearance('8px'),
      },
    })
    const checkoutInstance =
      result && typeof result.then === 'function' ? await result : result
    if (!checkoutInstance) {
      throw new Error('Could not initialize checkout.')
    }
    this.checkout = checkoutInstance
    const loadActionsResult = await checkoutInstance.loadActions()
    if (loadActionsResult?.type !== 'success') {
      throw new Error(
        loadActionsResult?.error?.message || 'Could not initialize checkout.',
      )
    }
    this.checkoutActions = loadActionsResult.actions
  }

  private bindCheckoutSessionChangeListener() {
    const checkout = this.checkout
    if (!checkout || typeof checkout.on !== 'function') {
      return
    }
    checkout.on('change', (session: any) => {
      // Stripe has no `off`, so old checkout instances keep firing after teardown.
      if (this.checkout !== checkout) {
        return
      }
      this.emitCheckoutSessionDisplay(session)
    })
  }

  private emitCheckoutSessionDisplay(session: any) {
    const totalAmount = session?.total?.total?.amount || ''
    const currency = session?.currency || ''
    if (!totalAmount) {
      return
    }
    this.$emit('checkout-session-change', { totalAmount, currency })
  }

  private async syncBuyerEmailWithCheckout() {
    const email = this.customerEmail?.trim()
    if (!email || !this.checkoutActions?.updateEmail) {
      return
    }
    try {
      const res = this.checkoutActions.updateEmail(email)
      if (res && typeof res.then === 'function') {
        await res
      }
    } catch (_) {
      // non-blocking
    }
  }

  private sessionHasCurrencyOptions(): boolean {
    const currencyOptions = this.checkoutActions?.getSession()?.currencyOptions
    return Array.isArray(currencyOptions) && currencyOptions.length > 0
  }

  private emitCurrencySelectorReady(ready: boolean) {
    this.$emit('currency-selector-availability', ready)
  }

  private holdCurrencySelectorSkeleton(): Promise<void> {
    return new Promise((resolve) =>
      setTimeout(resolve, CURRENCY_SELECTOR_SKELETON_HOLD_MS),
    )
  }

  private async mountCurrencySelector() {
    if (
      !this.checkout ||
      !this.checkoutActions ||
      !this.showCurrencySelector ||
      !this.externalCurrencySelectorTarget
    ) {
      return
    }
    this.emitCurrencySelectorReady(false)
    await this.$nextTick()
    try {
      const el =
        this.currentCurrencySelectorElement ||
        this.checkout.createCurrencySelectorElement()
      el.mount(this.externalCurrencySelectorTarget)
      this.currentCurrencySelectorElement = el
      await this.holdCurrencySelectorSkeleton()
      this.emitCurrencySelectorReady(true)
    } catch (_) {
      this.emitCurrencySelectorReady(false)
    }
  }

  /** Keeps the Stripe element alive while toggled off — only one per checkout. */
  private parkCurrencySelector() {
    if (this.currentCurrencySelectorElement) {
      try {
        this.currentCurrencySelectorElement.unmount()
      } catch (_) {
        // ignore
      }
    }
    this.emitCurrencySelectorReady(false)
  }

  private unmountCurrencySelector() {
    if (this.currentCurrencySelectorElement) {
      try {
        this.currentCurrencySelectorElement.unmount()
      } catch (_) {
        // ignore
      }
      this.currentCurrencySelectorElement = null
    }
    this.emitCurrencySelectorReady(false)
  }

  private async initializeAvailability() {
    this.unmountProbe()
    await this.$nextTick()

    this.availabilityReady = true
    this.$emit('available-methods', this.availableMethodsList)
    if (!this.selectedMethod) {
      this.selectedMethod =
        this.normalizeMethodId(this.selectedMethodId) || 'card'
    } else {
      await this.$nextTick()
      this.mountElementForMethod(this.selectedMethod)
    }

    const checkout = this.checkout
    const container = this.$refs.expressProbeContainer as HTMLElement
    if (!container || !checkout) {
      return
    }
    if (this.selectedMethod && this.selectedMethod !== 'card') {
      return
    }

    this.currentProbeExpressElement = checkout.createExpressCheckoutElement({
      buttonHeight: 48,
      layout: { maxColumns: 3, maxRows: 1 },
      paymentMethods: {
        link: 'auto',
        applePay: 'always',
        googlePay: 'auto',
        paypal: 'never',
        klarna: 'never',
      },
    })
    this.currentProbeExpressElement.mount(container)
    this.currentProbeExpressElement.on(
      'ready',
      ({ availablePaymentMethods }: any) => {
        this.availability = {
          card: true,
          apple_pay: !!availablePaymentMethods?.applePay,
          google_pay: !!availablePaymentMethods?.googlePay,
          link: !!availablePaymentMethods?.link,
        }
        this.availabilityReady = true
        this.$emit('available-methods', this.availableMethodsList)
        const preferred =
          this.normalizeMethodId(this.selectedMethodId) || this.selectedMethod
        const hasPreferred =
          !!preferred &&
          this.availableMethodsList.some((method) => method.id === preferred)
        this.selectedMethod = hasPreferred ? (preferred as MethodId) : 'card'
      },
    )
  }

  private async recreateSessionForAmount() {
    const runId = ++this.recreateSessionRunId
    this.teardownCheckout()
    await this.$nextTick()
    if (runId !== this.recreateSessionRunId) {
      return
    }
    await this.bootstrap(runId)
  }

  private scheduleRecreateSessionForAmount() {
    this.clearRecreateSessionDebounce()
    this.recreateSessionDebounceTimer = setTimeout(() => {
      this.recreateSessionDebounceTimer = null
      this.recreateSessionForAmount()
    }, RECREATE_SESSION_DEBOUNCE_MS)
  }

  private clearRecreateSessionDebounce() {
    if (!this.recreateSessionDebounceTimer) {
      return
    }
    clearTimeout(this.recreateSessionDebounceTimer)
    this.recreateSessionDebounceTimer = null
  }

  private teardownCheckout() {
    const checkout = this.checkout
    this.unmountElement()
    if (checkout) {
      this.destroyRegisteredBillingAddressElement(checkout)
    }
    this.unmountProbe()
    this.unmountCurrencySelector()
    this.availabilityReady = false
    this.checkout = null
    this.checkoutActions = null
    this.checkoutSessionId = null
    this.sessionClientSecret = null
    this.elementStripeLoading = false
    this.paymentElementEventsBound = false
  }

  private paymentMethodToStripeExpressKey(
    method: MethodId,
  ): 'applePay' | 'googlePay' | 'link' | null {
    if (method === 'apple_pay') {
      return 'applePay'
    }
    if (method === 'google_pay') {
      return 'googlePay'
    }
    if (method === 'link') {
      return 'link'
    }
    return null
  }

  private mountElementForMethod(method: MethodId) {
    const mountGen = ++this.mountGen
    this.elementStripeLoading = true
    const checkout = this.checkout
    const container = this.resolveElementMountContainer(method)
    if (!container || !checkout) {
      this.clearLoadingIfCurrent(mountGen)
      return
    }

    if (method === 'card') {
      if (this.currentPaymentElement) {
        this.unmountElement()
      }
      const existingPaymentElement = checkout.getPaymentElement?.()
      if (existingPaymentElement) {
        this.currentPaymentElement = existingPaymentElement
        existingPaymentElement.mount(container)
        existingPaymentElement.on('ready', () => {
          this.clearLoadingIfCurrent(mountGen)
        })
        if (!this.paymentElementEventsBound) {
          existingPaymentElement.on('change', (event: any) => {
            this.paymentElementComplete = !!event?.complete
            if (event?.error?.message) {
              this.$emit('on-card-error', [event.error.message])
            } else {
              this.$emit('on-card-error', [])
            }
          })
          this.paymentElementEventsBound = true
        }
        this.mountBillingAddressElement(checkout)
        return
      }
      this.currentPaymentElement = checkout.createPaymentElement({
        layout: {
          type: 'tabs',
        },
        paymentMethodOrder: ['card'],
        wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
        fields: {
          billingDetails: {
            name: 'never',
            address: 'never',
          },
        },
      })
      this.currentPaymentElement.mount(container)
      this.currentPaymentElement.on('ready', () => {
        this.clearLoadingIfCurrent(mountGen)
      })
      this.currentPaymentElement.on('change', (event: any) => {
        this.paymentElementComplete = !!event?.complete
        if (event?.error?.message) {
          this.$emit('on-card-error', [event.error.message])
        } else {
          this.$emit('on-card-error', [])
        }
      })
      this.paymentElementEventsBound = true
      this.mountBillingAddressElement(checkout)
      return
    }

    // One Express Checkout element per checkout instance.
    this.destroyProbeExpressElement()
    this.destroyRegisteredExpressElement(checkout)

    const expressKey = this.paymentMethodToStripeExpressKey(method)
    if (!expressKey) {
      this.clearLoadingIfCurrent(mountGen)
      return
    }
    const paymentMethods: any = {
      link: 'never',
      applePay: 'never',
      googlePay: 'never',
      paypal: 'never',
      klarna: 'never',
    }
    paymentMethods[expressKey] = expressKey === 'applePay' ? 'always' : 'auto'

    this.currentExpressElement = checkout.createExpressCheckoutElement({
      buttonHeight: 48,
      layout: { maxColumns: 1, maxRows: 1 },
      paymentMethods,
    })
    this.currentExpressElement.mount(container)
    this.currentExpressElement.on('ready', () => {
      this.clearLoadingIfCurrent(mountGen)
    })
    this.currentExpressElement.on('confirm', (event: any) => {
      this.handleExpressConfirm(event)
    })
  }

  private resolveElementMountContainer(method: MethodId): HTMLElement | null {
    if (method === 'card') {
      return this.$refs.elementContainer as HTMLElement
    }
    return this.externalExpressElementContainer
  }

  private mountBillingAddressElement(checkout: any) {
    const container = this.$refs.billingContainer as HTMLElement
    if (!container || !checkout?.createBillingAddressElement) {
      return
    }
    const existingBillingAddressElement = checkout.getBillingAddressElement?.()
    if (existingBillingAddressElement) {
      this.currentBillingAddressElement = existingBillingAddressElement
      existingBillingAddressElement.mount(container)
      return
    }
    this.destroyBillingAddressElement()
    this.currentBillingAddressElement = checkout.createBillingAddressElement()
    this.currentBillingAddressElement.mount(container)
  }

  private destroyBillingAddressElement() {
    if (this.currentBillingAddressElement) {
      try {
        this.currentBillingAddressElement.unmount()
      } catch (_) {
        // ignore
      }
      this.currentBillingAddressElement = null
    }
  }

  private destroyRegisteredBillingAddressElement(checkout: any) {
    const registered = checkout.getBillingAddressElement?.()
    if (!registered) {
      return
    }
    this.destroyExpressCheckoutDomElement(registered)
    if (this.currentBillingAddressElement === registered) {
      this.currentBillingAddressElement = null
    }
  }

  private async handleExpressConfirm(event: any) {
    if (!this.paymentInteractionEnabled) {
      return
    }
    if (!this.termsAccepted) {
      this.$emit('on-card-error', ['Please accept the Terms & Conditions'])
      return
    }
    if (!this.checkoutActions || !this.checkoutSessionId) {
      return
    }
    if (!(await this.passesAmountPrecheck())) {
      return
    }
    try {
      const result = await this.checkoutActions.confirm({
        redirect: 'if_required',
        expressCheckoutConfirmEvent: event,
      })
      if (this.isErrorResult(result)) {
        const msg = this.extractErrorMessage(result)
        notification(msg, 'error')
        this.$emit('on-card-error', [msg])
        return
      }
      this.emitCheckoutComplete()
    } catch (err: any) {
      const msg = err?.message || 'Payment failed'
      notification(msg, 'error')
      this.$emit('on-card-error', [msg])
    }
  }

  private clearLoadingIfCurrent(mountGen: number) {
    if (mountGen === this.mountGen) {
      this.elementStripeLoading = false
      this.syncExternalExpressLoadingState()
    }
  }

  private destroyRegisteredExpressElement(checkout: any) {
    const registered = checkout.getExpressCheckoutElement?.()
    if (!registered) {
      return
    }
    this.destroyExpressCheckoutDomElement(registered)
    if (this.currentExpressElement === registered) {
      this.currentExpressElement = null
    }
    if (this.currentProbeExpressElement === registered) {
      this.currentProbeExpressElement = null
    }
  }

  private destroyExpressCheckoutDomElement(element: any) {
    try {
      if (typeof element.destroy === 'function') {
        element.destroy()
        return
      }
      element.unmount()
    } catch (_) {
      // ignore
    }
  }

  private destroyProbeExpressElement() {
    if (!this.currentProbeExpressElement) {
      return
    }
    this.destroyExpressCheckoutDomElement(this.currentProbeExpressElement)
    this.currentProbeExpressElement = null
  }

  private unmountProbe() {
    this.destroyProbeExpressElement()
  }

  private destroyExpressElement() {
    if (!this.currentExpressElement) {
      return
    }
    this.destroyExpressCheckoutDomElement(this.currentExpressElement)
    this.currentExpressElement = null
  }

  private unmountElement() {
    if (this.currentPaymentElement) {
      try {
        this.currentPaymentElement.unmount()
      } catch (_) {
        // ignore
      }
      this.currentPaymentElement = null
    }
    this.destroyBillingAddressElement()
    this.destroyExpressElement()
    this.paymentElementComplete = false
  }
}
